import { IActionFn } from '@/store/types';
import { IModuleState, ITaskMap } from './types';
import { uniqueList } from '@/utils/object';
import { $http } from '@/axios';
import store from '@/store';

// 生成物品或材料列表（附带品牌信息）
function generateMatList(list: any[], typeName: string) {
  return list.map((item) => ({
    id: item.id,
    type: typeName,
    code: item.code,
    name: item.name,
    brandId: item.brandId || item.id,
    brandCode: item.brandCode || item.code,
    brandName: item.brandName || item.nameChn,
  }));
}

function normalizeCheckList(type: string, res: any) {
  if (type === 'designshow') {
    const designShowBrandData = Array.isArray(res?.designBrandCheckList) ? res.designBrandCheckList : [];
    return {
      data: designShowBrandData,
      requiresDesignMigration: !!res?.requiresDesignMigration,
      canSkipDesignMigration: res?.canSkipDesignMigration !== false,
      missingBrandCount: typeof res?.missingBrandCount === 'number' ? res.missingBrandCount : designShowBrandData.length,
    };
  }

  const defaultBrandData = Array.isArray(res) ? res : [];
  return {
    data: defaultBrandData,
    requiresDesignMigration: false,
    canSkipDesignMigration: false,
    missingBrandCount: defaultBrandData.length,
  };
}

// 查询迁移任务列表
export const queryMigrateTasks: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setMigrateTasks', { ...state.taskList, loading: true });
    const migrateEnvs: any[] = store.getters['data/common/getMigrateEnvs'];
    const migrateTypes: any[] = store.getters['data/common/getMigrateTypes'];
    const envName = (env: string) => migrateEnvs.filter((item) => item.value === env)[0]?.text || '';
    const typeName = (type: string) => migrateTypes.filter((item) => item.value === type.toLowerCase())[0]?.text || '';
    // const res: ITaskMap = await $http.get(  '/migration/input/requests');
    const res: ITaskMap = await $http.get(  '/v1/migration/input/requests');
    const taskList = Object.keys(res).reduce((acc, cur) => {
      const tName = typeName(cur);
      const dtoList = res[cur] || [] as any[];
      const list = dtoList.map((item) => ({ ...item, typeName: tName, envName: envName(item.env) }));
      return [...acc, ...list];
    }, [] as any[]).filter((item) => item.migrationType !== 'statictexturepaks'); // 去掉老版本的静态贴图包任务列表;
    commit('setMigrateTasks', { data: taskList });
  } catch (e) {
    commit('setMigrateTasks', { data: [] });
  }
};

// 查询迁移任务详情(子列表)
export const queryTaskDetail: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    const type = payload.migrationType;
    const migrateTypes: any[] = store.getters['data/common/getMigrateTypes'];
    const dto = migrateTypes.filter((item) => item.value === type)[0]?.dto || 0;
    commit('setMatList', { data: [], loading: true });
    commit('setTaskDetail', { data: [], loading: true });
    // const res: any = await $http.get(`/migration/input/request/${type}/${payload.uuid}`);
    const res: any = await $http.get(`/v1/migration/input/request/${type}/${payload.uuid}`);

    // 任务详情
    switch (type) {
      case 'modelitem':
        // 这个迁移类型是由多个 dto 组成的列表
        const models = res?.modelTypeItemDtos || [];          // 模型
        const blocks = res?.furnitureModuleDtos || [];        // 模块
        const components = res?.furnitureComponentDtos || []; // 零件
        const props = res?.stagePropertyDtos || [];           // 简单道具
        const propBlocks = res?.propModuleDtos || [];         // 复杂道具
        commit('setTaskDetail', { data: [...models, ...components, ...blocks, ...props, ...propBlocks] });
        break;
      case 'digitalproducts':
      case 'sweepsections':
        // 接口源数据是字符串数组，需要预处理为表格组件所需的数据格式
        const list = res[dto] as string[];
        const data = list.map((item, index) => ({ id: index + 1, name: item }));
        commit('setTaskDetail', { data });
        break;
      default:
        // 直接用接口源数据
        commit('setTaskDetail', { data: res[dto] });
        break;
    }

    // 提取物料列表
    switch (type) {
      case 'modelitem':
        // 提取模型, 零件，模块，简单道具，复杂道具的物料列表
        const modelList = generateMatList(res?.modelTypeItemDtos || [], '物品');
        const blockList = generateMatList(res?.furnitureModuleDtos || [], '模块');
        const componentList = generateMatList(res?.furnitureComponentDtos || [], '零件');
        const propList = generateMatList(res?.stagePropertyDtos || [], '简单道具');
        const propBlockList = generateMatList(res?.propModuleDtos || [], '复杂道具');
        commit('setMatList', { data: [...modelList, ...componentList, ...blockList, ...propList, ...propBlockList] });
        break;
      case 'materialitem':
        // 提取材料的物料列表
        commit('setMatList', { data: generateMatList(res[dto], '材料') });
        break;
      case 'design':
        // 提取专案的物料列表
        const designModels = res.modelTypeItemDtos || [];
        const designMaterials = res.materialTypeItemDtos || [];
        const designModelList = generateMatList(designModels, '物品');
        const designMaterialList = generateMatList(designMaterials, '材料');
        commit('setMatList', { data: [...designModelList, ...designMaterialList] });
        break;
      case 'openworld':
        // 提取开放世界的品牌列表（数据结构跟上面的物料列表保持一致）
        const openWorlds = (res[dto] || []) as any[];
        const brands = openWorlds.reduce((acc, cur) => [...acc, ...(cur.brands || [])], []);
        const openWorldList = generateMatList(uniqueList(brands, 'id'), '开放世界');
        commit('setMatList', { data: openWorldList });
        break;
      case 'sectionproduct':
        // 提取型材产品里的品牌列表（数据结构跟上面的物料列表保持一致）
        const sectionProds = (res[dto] || []) as any[];
        const sectionProdList = generateMatList(sectionProds, '型材产品');
        commit('setMatList', { data: [...sectionProdList] });
        break;
      default:
        commit('setMatList', { data: [] });
        break;
    }
  } catch (e) {
    commit('setMatList', { data: [] });
    commit('setTaskDetail', { data: [] });
  }
};

// 查询当前任务是否正在后台运行
export const checkTaskStatus: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    const uuid = payload.uuid;
    commit('setTaskCheck', { uuid, result: false, loading: true });
    // true 表示任务正在运行; false 表示任务等待运行; null 表示任务还没创建或者已经结束了
    // const res: boolean | null = await $http.post(  '/migrate/status/check', payload);
    const res: boolean | null = await $http.post(  '/v1/migration/status/check', payload);
    commit('setTaskCheck', { uuid, result: res === true });
  } catch (e) {
    commit('setTaskCheck', { uuid: '', result: false });
  }
};

// 删除迁移任务
export const deleteTask: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    // await $http.post(  '/migrate/delete', payload);
    await $http.post(  '/v1/migration/delete', payload);
    const taskList = state.taskList.data.filter((item) => item.uuid !== payload.uuid);
    commit('setMigrateTasks', { data: taskList });
    store.commit('common/setToast', { visible: true, text: '删除成功' });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 迁移之前的品牌检查(查询当前环境不存在的品牌列表)
export const checkBrands: IActionFn<IModuleState> = async ({ commit, state, getters }, payload) => {
  try {
    const type = payload.migrationType;
    const migrateTypes: any[] = store.getters['data/common/getMigrateTypes'];
    const postUrl = migrateTypes.filter((item) => item.value === type)[0]?.checkUrl || '';
    commit('setCheckList', {
      data: [],
      loading: true,
      completed: false,
      requiresDesignMigration: false,
      canSkipDesignMigration: false,
      missingBrandCount: 0,
    });
    const res: any = await $http.post(postUrl, payload, { ignoreCode: [18000] });
    if (res?.rtnCode === 18000) {
      const uuid = payload.uuid;
      const taskList = state.taskList.data.filter((item) => item.uuid !== uuid);
      commit('setMigrateTasks', { data: taskList });  // 将已经完成的迁移任务从列表里移除
      commit('setCheckList', { ...state.checkList, completed: true });
      store.commit('common/setToast', { visible: true, text: res?.message });
    } else {
      commit('setCheckList', normalizeCheckList(type, res));
    }
  } catch (e) {
    commit('setCheckList', {
      data: [],
      requiresDesignMigration: false,
      canSkipDesignMigration: false,
      missingBrandCount: 0,
    });
  }
};

// 查询静态材质列表
export const queryTextures: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setTextures', { data: [], raw: [], loading: true });
    // const { result = [] as any[] } = await $http.post(  '/migration/output/statictexturepaks', payload);
    const { result = [] as any[] } = await $http.post(  '/v1/migration/output/statictexturepaks', payload);
    const list = result.map((item: any, index: number) => ({ ...item, id: index + 1 }));
    commit('setTextures', { data: [...list], raw: list });
  } catch (e) {
    commit('setTextures', { data: [], raw: [] });
  }
};

// 删除静态材质
export const deleteTexture: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post(  '/migration/output/statictexturepaks/delete', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 刷新静态资源列表(主要用于解决客户端静态资源列表无法及时更新的问题)
export const refreshTexture: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setLoading', true);
    // await $http.post(  '/migration/output/ueitem/syntexturelist', payload);
    await $http.post(  '/v1/migration/output/ueitem/syntexturelist', payload);
    store.commit('common/setToast', { visible: true, text: '静态资源列表刷新成功' });
  } finally {
    commit('setLoading', false);
  }
};

// 查询灯光配置列表
export const queryIesList: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setIesList', { data: [], raw: [], loading: true });
    // const { result = [] as any[] } = await $http.post(  '/migration/output/ies', payload);
    const { result = [] as any[] } = await $http.post(  '/v1/migration/output/ies', payload);
    const list = result.map((item, index) => ({ ...item, id: index + 1 }));
    commit('setIesList', { data: [...list], raw: list });
  } catch (e) {
    commit('setIesList', { data: [], raw: [] });
  }
};

// 物品品牌迁移(同环境下的品牌互换)
export const migrateModelBrand: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setLoading', true);
    await $http.post(  '/v1/migration/migrationitembrand', payload);
    store.commit('common/setToast', { visible: true, text: '物品品牌迁移成功' });
  } finally {
    commit('setLoading', false);
  }
};

// 材料品牌迁移(同环境下的品牌互换)
export const migrateMaterialBrand: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setLoading', true);
    await $http.post(  '/v1/migration/migrationmaterialbrand', payload);
    store.commit('common/setToast', { visible: true, text: '材料品牌迁移成功' });
  } finally {
    commit('setLoading', false);
  }
};
